import { parsePhoneNumberFromString } from 'libphonenumber-js/min';
import type {
  BillingUnit,
  Currency,
  Language,
  SubscriptionGrantMode,
  TaxInfo,
} from '@hero/schema';
import { getPlanBusiness, planUnit, type Business } from './app-registry.config';

/**
 * Subscription catalog (v1 lives in config; promote to DB when sales needs
 * live edits).
 *
 * IMPORTANT: All prices are human-readable decimals in the currency
 * indicated by the inner key (SAR or USD).
 *
 * PRICING MODEL — per-month rates indexed by billing interval.
 * Each `*PerMonth` field is a **per-month** rate that varies by the
 * interval the customer commits to. Yearly is cheaper per month than
 * monthly (built-in commitment discount); semester sits in between.
 *
 * Per-period total = perMonth[interval] × monthsInInterval, where
 * monthsInInterval is 1 / 6 / 12. This is the only multiplication used
 * by `computeSubscriptionPreview` and `BillingPricingService` — they
 * never multiply per-currency rates by anything else.
 *
 * BILLING UNIT — a plan meters either vehicle seats or order volume
 * (`BillingUnit` via {@link planUnit}). Seat plans are `period`
 * subscriptions; order-credit plans are `usage`. Branch on
 * {@link getPlanSubscriptionModel} / `planUnit`.
 *
 * The moyasar submodule converts to minor units (halalas / cents / fils)
 * internally before POST /v1/payments and converts responses back. Callers
 * never deal with minor units. See `apps/backend/src/app/sales/moyasar/README.md`.
 *
 * Per-currency prices are INDEPENDENT — they are not FX conversions of
 * each other. Set them based on market psychology, not exchange rates.
 */
/**
 * Billing intervals self-serve subscriptions can pick.
 *
 * `'week'` was dropped in 2026-06: weekly Moyasar charges generated
 * too much processor noise (failed-payment retries, prorations on
 * every plan change) for too little ARR signal. Custom enterprise
 * deals that need shorter cycles live on `Subscription.pricingOverride`
 * with their own arbitrary billing cadence, not on this union.
 */
export type SubscriptionInterval = 'month' | 'semester' | 'year';
export type AddonCadence = 'oneTime' | SubscriptionInterval;

/**
 * Business (brand) assumed for a plan code no product claims — the
 * dashboard ladder. Plans are claimed in `PRODUCT_REGISTRY`; brand is
 * read back via {@link getPlanBusiness}.
 */
export const DEFAULT_SUBSCRIPTION_BUSINESS: Business = 'hero';

/**
 * Who is buying — a company or a one-person operation.
 *
 * A freelancer driver and a carrier company reach the same product
 * through the same ladder shape, but they are not the same buyer. One
 * is spending their own money on the documents they personally need to
 * drive today; the other is spending a company budget on capacity for a
 * fleet. Sooner or later those get different numbers, and the ladder
 * has to be able to express that without a second pricing mechanism
 * bolted alongside it.
 *
 * So the audience is a key on the ladder table rather than a discount
 * applied to a shared one. A discount would mean the freelancer price
 * is forever derived from the company price, and the two would have to
 * move together — which is exactly the coupling we do not want.
 *
 * Derived from who owns the subscription, never asked: a subscription
 * owned by a `driver` Party is a freelancer's, one owned by an `org` is
 * a company's. See `getSubscriptionAudience` in `@hero/schema`.
 */
export type SubscriptionAudience = 'org' | 'freelancer';

/**
 * Audience assumed when a caller does not say. Companies are the
 * default because every pre-freelancer subscription is one, so an
 * un-migrated read is right rather than merely safe.
 */
export const DEFAULT_SUBSCRIPTION_AUDIENCE: SubscriptionAudience = 'org';

type PerIntervalPrice = Readonly<Record<SubscriptionInterval, number>>;
type PerCurrencyPerInterval = Readonly<Partial<Record<Currency, PerIntervalPrice>>>;
type PerCurrencyPerCadence = Readonly<
  Partial<Record<Currency, Readonly<Partial<Record<AddonCadence, number>>>>>
>;

type PlanFeatures = Readonly<{
  /** Maximum allowed vehicles. Soft cap on paid plans (overage billed); hard cap on free. */
  maxVehicles?: number;
  /** 'soft' = allow exceeding committed and bill overage; 'hard' = refuse new vehicles. */
  maxVehiclesPolicy?: 'soft' | 'hard';
  /**
   * Maximum orders per billing period. Soft cap on per-order plans
   * (overage billed); hard cap refuses new orders once reached.
   */
  maxOrders?: number;
  /** 'soft' = allow exceeding committed and bill overage; 'hard' = refuse new orders. */
  maxOrdersPolicy?: 'soft' | 'hard';
  /**
   * Orders are not metered at all while the subscription is active — the
   * per-truck monthly plans. What a quote says next to the price.
   */
  unlimitedOrders?: boolean;
  /** Which modules are unlocked. */
  modules?: Readonly<{
    order?: boolean;
    fleet?: boolean;
    crm?: boolean;
    api?: boolean;
  }>;
  /** Third-party integrations unlocked. */
  integrations?: ReadonlyArray<'whatsapp' | 'api'>;
  /**
   * Support level the plan card advertises.
   *
   * Lives here rather than as an `if (planCode === …)` chain in the
   * card, because that chain is how the per-order ladder shipped with
   * no support bullet at all: three new plans were added to this file
   * and the component never heard about them. A plan that promises a
   * support level says so next to its price.
   */
  supportTier?: 'email' | 'standard' | 'priority' | 'dedicated';
}>;

/**
 * One feature tier.
 *
 * **A plan does not declare its business or its meter.** Both used to
 * live here as `lines?` / `billingBasis?`. The product registry owns
 * both facts now: `PRODUCT_REGISTRY[product].planCodes` lists the tiers
 * a product sells, and {@link getPlanBusiness} / {@link planUnit} read
 * that mapping backwards.
 */
type PlanConfig = Readonly<{
  features: PlanFeatures;
  /**
   * Hidden from every plan picker — the dashboard grid and the agent's
   * quotes alike.
   *
   * `free` produces no Billing and no payment link; orgs land on it
   * through trial expiry or an admin grant, never by choosing it. A
   * card nobody can act on is a dead end in a chooser.
   */
  unlisted?: boolean;
  /**
   * Priced by a human, not by this file.
   *
   * `custom` rates live on a negotiated per-Org
   * `Subscription.pricingOverride`, so there is no number to quote
   * until sales agrees one. The dashboard still renders it — as a
   * "contact sales" card, which is a conversation rather than a
   * purchase — while chat leaves it out entirely, because a model that
   * can see the plan will eventually quote a price for it.
   */
  contactSales?: boolean;
  /**
   * The tier to lead with on this basis — the "most popular" card, and
   * what a picker pre-selects when the buyer has expressed no
   * preference.
   *
   * At most one per (line, basis). Which plan we push is a commercial
   * decision that belongs beside the prices, not in a `PLAN_META` map
   * in one component and a `defaultPlanCode = 'growth'` string in a
   * hook, which is where it lived and how the per-order ladder ended
   * up with no default at all.
   */
  recommended?: boolean;
  /**
   * Plan base price **per month**, per currency, indexed by the
   * commitment interval the customer is paying for. Yearly's per-month
   * rate is intentionally < monthly's so that a year commit is cheaper
   * than 12 × monthly. Optional on `free`.
   *
   * Basis-independent: charged on both `per_vehicle` and `per_order`
   * plans, on top of the metered quantity.
   */
  basePricePerMonth?: PerCurrencyPerInterval;
  /**
   * Per-vehicle (committed seat) price **per month**, per currency,
   * indexed by the commitment interval. Optional on `free`.
   *
   * Only read on `per_vehicle` plans.
   */
  perVehiclePricePerMonth?: PerCurrencyPerInterval;
  /**
   * Per-vehicle overage charge **per month** (when fleet > committed),
   * per currency, indexed by the commitment interval. Optional on `free`.
   *
   * Only read on `per_vehicle` plans.
   */
  overagePerVehiclePerMonth?: PerCurrencyPerInterval;
}>;

/**
 * One step of a volume ladder for buying order credits.
 *
 * Buying credits is priced by **how many you buy**, not by which plan
 * you are on. That is the whole shape of the offer: 10 orders cost
 * 7 SAR each, 1000 cost 3 SAR each, and a customer sitting on the top
 * feature tier who tops up 10 pays the same 7 as anyone else. Pricing
 * the top-up by plan instead would mean the cheapest way to buy a
 * small bucket is to first downgrade, which is a rule that teaches
 * customers to game it.
 *
 * The plan still matters — it is what the credits unlock — which is
 * why each step names one. Reaching a step *grants* its plan and never
 * takes one away (see {@link resolveUsagePlanUpgrade}): buying 1000
 * moves you up, and topping up 10 afterwards does not move you back
 * down. Features you have paid for once do not evaporate because your
 * next purchase was small.
 */
export type UsageTierConfig = Readonly<{
  /** Smallest purchase that reaches this step. */
  minQty: number;
  /** The plan this step grants — see {@link resolveUsagePlanUpgrade}. */
  planCode: string;
  /**
   * Published price of one credit at this step. Whether VAT is already
   * inside this number is {@link UsageAudiencePricing.taxInclusive} on
   * the audience that owns this rung — not implied by the field name.
   */
  pricePerUnit: Readonly<Partial<Record<Currency, number>>>;
  /**
   * Marks the rung to point a buyer at — rendered as a "best value"
   * badge wherever the ladder is quoted.
   *
   * Optional and unset by default: a ladder where nothing is
   * highlighted is a price list, and that is the honest default until
   * someone decides which rung the business wants to sell. Set it on at
   * most one rung per ladder; the renderers badge every rung that
   * carries it and four badges recommend nothing.
   */
  highlight?: boolean;
}>;

/**
 * How the ladder card prints a rung. Charge math is always
 * `pricePerUnit × qty` — this only changes what the buyer reads first.
 *
 * - `per_unit` — "7 per وثيقة". The rate is the headline.
 * - `packaged` — "35 for 5 وثائق". The floor of the rung times the
 *   rate is the headline, so a buyer sees the cash outlay for the
 *   smallest purchase that reaches it rather than a unit price they
 *   then have to multiply.
 */
export type UsagePriceDisplayMode = 'per_unit' | 'packaged';

/**
 * One audience's credit catalog: the rungs they see, how those
 * published rates are taxed, and how the card leads each rung.
 *
 * `taxInclusive` is an interpretation of every `pricePerUnit` on this
 * ladder, not a per-rung flag — mixing tax-in and tax-out on the same
 * list would make the card and the invoice disagree.
 *
 * `displayMode` is presentation. A packaged card that quotes 35 for 5
 * still charges 7 × the quantity they name.
 */
export type UsageAudiencePricing = Readonly<{
  /**
   * When `true`, each `pricePerUnit` is what the buyer pays (VAT
   * inside). When `false`, it is pre-tax and VAT is added on top.
   */
  taxInclusive: boolean;
  displayMode: UsagePriceDisplayMode;
  tiers: ReadonlyArray<UsageTierConfig>;
}>;

/**
 * The credits a fresh account is handed before it has paid — the
 * generalised, per-audience form of the old fixed `freeAllowance`.
 *
 * - `qty` — how many credits the signup grant is worth.
 * - `mode` — `repayable` reproduces the classic "try it, then pay it
 *   back" overdraft (the next purchase settles it); `free` is a true
 *   gift that is never charged.
 * - `expiresInDays` — optional. Set it for a time-limited promo; omit
 *   it for a grant that lives until the credits are spent.
 *
 * Catalog default here; a PostHog pricing flag (`wathiqa_plus_pricing`,
 * and a future `hero_pricing`) can overlay it per audience without a
 * deploy — see `resolveFreeCreditsPolicy`.
 */
export type FreeCreditsPolicy = Readonly<
  | {
      by?: 'orders';
      qty: number;
      mode: SubscriptionGrantMode;
      expiresInDays?: number;
    }
  | {
      /** Every order free for `days` days, whichever plan they pick. */
      by: 'days';
      days: number;
      /** When the days start counting. */
      startsOn: FreeWindowStart;
    }
>;

/**
 * - `first_waybill` — an account that signs up and waits keeps its days.
 * - `signup` — the days run from account creation.
 */
export type FreeWindowStart = 'first_waybill' | 'signup';

type AddonConfig = Readonly<{
  /**
   * - `recurring_only` — must be purchased as a recurring addon.
   * - `recurring_or_one_time` — can be one-time or recurring.
   */
  type: 'recurring_only' | 'recurring_or_one_time';
  /**
   * Pricing keyed by cadence:
   * - `oneTime` is a single-shot total (not per month).
   * - `month` / `semester` / `year` are **per-month** rates at that
   *   commitment cadence. Per-period total = perMonth × monthsInInterval.
   */
  pricing: PerCurrencyPerCadence;
  features: PlanFeatures;
}>;

/**
 * Which action the paywall blocks — see {@link ENTITLEMENT_GATE.firesAt}.
 */
export type EntitlementGatePoint =
  /** Nothing works until they pay. */
  | 'account_created'
  /** They can build an order; saving it is what costs. */
  | 'before_order_commit'
  /** The whole order goes through; the government filing is what costs. */
  | 'before_waybill_issue';

type EntitlementGateConfig = Readonly<{
  /**
   * `false` disables the gate entirely — nothing is ever blocked. Here
   * so the paywall can be switched off without unpicking the call
   * sites, and so it ships dark.
   */
  enabled: boolean;
  firesAt: EntitlementGatePoint;
  /**
   * How many actions pass before the gate starts blocking. `0` blocks
   * from the first one.
   *
   * Counted **org-scoped**, not per conversation — counting per thread
   * would hand a fresh allowance to anyone who messages from a second
   * phone.
   */
  freeAllowance: number;
  /** Whether `status: 'trialing'` is enough to pass. */
  trialCountsAsPaid: boolean;
}>;

/**
 * The paywall — when a company that has not paid stops being able to
 * work, and how much it gets for free first.
 *
 * Two knobs rather than one placement, because where the gate belongs
 * is a question testing answers, not code. The three placements worth
 * testing are combinations of them: "after account" is
 * `account_created` + 0, "before committing" is `before_order_commit` +
 * 0, "after the first order" is `before_waybill_issue` + 1. Same code
 * path each time, so moving the paywall is a config edit rather than a
 * rewrite of the funnel.
 *
 * `trialCountsAsPaid: false` is what makes this a hard block: passing
 * requires `status === 'active'`, which only a settled Moyasar payment
 * produces. Flip it to `true` and a trial walks through, which turns
 * the gate into a soft prompt.
 *
 * Typed rather than `as const` on purpose: the consuming code in
 * `SubscriptionEntitlementService` must stay live when a flag is
 * flipped, and literal types would narrow half of it to dead branches.
 */
const ENTITLEMENT_GATE: EntitlementGateConfig = {
  enabled: true,
  firesAt: 'before_waybill_issue',
  freeAllowance: 1,
  trialCountsAsPaid: false,
};

/**
 * The volume ladders for buying order credits — one per business, per
 * audience. Each audience owns its rungs, whether those rates already
 * include VAT, and how the card leads each rung, so editing a freelancer
 * Wathiqa price does not mean jumping to a second table.
 *
 * Keyed by business (brand) because each brand sells one credit ladder.
 * Seat plans share the same business but have no ladder — callers that
 * ask for credits on a seat-only path get an empty array.
 *
 * Ascending by `minQty`; a purchase is priced by the last step it
 * reaches, so 300 credits price at the 250 step.
 */
const USAGE_ORDER_TIERS: Readonly<
  Record<Business, Readonly<Record<SubscriptionAudience, UsageAudiencePricing>>>
> = {
  hero: {
    org: {
      taxInclusive: false,
      displayMode: 'per_unit',
      tiers: [
        { minQty: 10, planCode: 'start', pricePerUnit: { SAR: 7, USD: 1.9 } },
        { minQty: 100, planCode: 'pro', pricePerUnit: { SAR: 5, USD: 1.35 } },
        { minQty: 250, planCode: 'pro', pricePerUnit: { SAR: 4, USD: 1.1 } },
        { minQty: 1000, planCode: 'max', pricePerUnit: { SAR: 3, USD: 0.8 } },
      ],
    },
    freelancer: {
      taxInclusive: false,
      displayMode: 'per_unit',
      tiers: [
        { minQty: 10, planCode: 'start', pricePerUnit: { SAR: 7, USD: 1.9 } },
        { minQty: 100, planCode: 'pro', pricePerUnit: { SAR: 5, USD: 1.35 } },
        { minQty: 250, planCode: 'pro', pricePerUnit: { SAR: 4, USD: 1.1 } },
        { minQty: 1000, planCode: 'max', pricePerUnit: { SAR: 3, USD: 0.8 } },
      ],
    },
  },
  wathiqa_plus: {
    org: {
      taxInclusive: false,
      displayMode: 'per_unit',
      tiers: [
        { minQty: 5, planCode: 'wathiqa_start', pricePerUnit: { SAR: 7, USD: 1.9 } },
        { minQty: 50, planCode: 'wathiqa_pro', pricePerUnit: { SAR: 5, USD: 1.35 } },
        { minQty: 500, planCode: 'wathiqa_max', pricePerUnit: { SAR: 3, USD: 1.1 } },
      ],
    },
    freelancer: {
      taxInclusive: false,
      displayMode: 'per_unit',
      tiers: [
        { minQty: 5, planCode: 'wathiqa_start', pricePerUnit: { SAR: 7, USD: 1.9 } },
        { minQty: 10, planCode: 'wathiqa_pro', pricePerUnit: { SAR: 5, USD: 1.35 } },
        { minQty: 30, planCode: 'wathiqa_max', pricePerUnit: { SAR: 3, USD: 0.8 } },
      ],
    },
  },
};

/**
 * Signup free-credit policy per business, per audience.
 *
 * Seeded to match the historical `entitlementGate.freeAllowance: 1`
 * behaviour — one repayable credit — so nothing changes for a fresh
 * account until a PostHog overlay or an admin gift says otherwise.
 * Keyed by business (brand) like {@link USAGE_ORDER_TIERS}.
 */
const FREE_CREDITS: Readonly<
  Record<Business, Readonly<Record<SubscriptionAudience, FreeCreditsPolicy>>>
> = {
  hero: {
    org: { qty: 1, mode: 'repayable' },
    freelancer: { qty: 1, mode: 'repayable' },
  },
  wathiqa_plus: {
    org: { qty: 1, mode: 'repayable' },
    freelancer: { qty: 1, mode: 'repayable' },
  },
};

/**
 * The two ways a business sells: a prepaid bundle of orders priced by the
 * volume ladder, or a monthly per-truck subscription. Derived from the
 * kinds in {@link SalesConfig.plans}, in the order they are presented.
 */
export type PricingOffer = 'per_order' | 'subscription';

/**
 * How the conversation gets from "how much?" to an invoice.
 *
 * - `choose_model` — ask which offer they want, then ask its quantity
 *   (trucks or orders). Skips the question when only one offer is sold.
 * - `menu` — present a short fixed list of options; the one they pick is
 *   invoiced as-is.
 */
export type PricingFlow = 'choose_model' | 'menu';

/**
 * What a new subscriber gets before paying the first invoice.
 *
 * - `none`   — nothing; the plan unlocks once the invoice is paid.
 * - `days`   — the plan works for `days` days from subscribe.
 * - `orders` — the plan covers the first `orders` orders.
 *
 * Either way the invoice is issued at subscribe and can be paid at any
 * point; paying ends the trial. Once it runs out unpaid, the paywall
 * asks for that invoice.
 */
export type TrialPolicy = Readonly<
  { by: 'none' } | { by: 'days'; days: number } | { by: 'orders'; orders: number }
>;

/**
 * Paying per order off the volume ladder. At most one per audience.
 * Fields left out fall back to the catalog ladder in
 * {@link USAGE_ORDER_TIERS}.
 */
export type PerOrderPlanConfig = Readonly<{
  id: string;
  kind: 'per_order';
  tiers?: ReadonlyArray<UsageTierConfig>;
  displayMode?: UsagePriceDisplayMode;
  /** Whether each `pricePerUnit` already includes VAT. */
  taxInclusive?: boolean;
}>;

/**
 * Unlimited orders for a monthly price per truck. An audience can sell
 * several (different price, trial or truck range); they all bill on the
 * same catalog seat plan.
 *
 * The price lives here rather than on the plan so each audience (and
 * each PostHog variant) carries its own number. Checkout stamps the
 * quoted price on the subscription as its `pricingOverride`, so renewals
 * bill what was quoted even after this number moves.
 */
export type UnlimitedPlanConfig = Readonly<{
  id: string;
  kind: 'unlimited';
  /** What the buyer hears it called, per language. */
  name?: Readonly<Partial<Record<Language, string>>>;
  /**
   * Catalog seat plan with `unlimitedOrders`. Absent means the
   * business's own (`wathiqa_unlimited`).
   */
  planCode?: string;
  /** Monthly price of one truck, per currency. */
  perVehiclePricePerMonth: Readonly<Partial<Record<Currency, number>>>;
  /** Whether `perVehiclePricePerMonth` already includes VAT. */
  taxInclusive?: boolean;
  minTrucks?: number;
  maxTrucks?: number;
  highlight?: boolean;
}>;

/** The business's tiered seat plans, priced from the plan catalog (the Hero line). */
export type SeatPlansConfig = Readonly<{ id: string; kind: 'seats' }>;

export type SalesPlanConfig = PerOrderPlanConfig | UnlimitedPlanConfig | SeatPlansConfig;

/**
 * One option of a `menu` flow, pointing at a plan by `id`: an order
 * count for the per-order plan, a truck count for an unlimited one.
 * Priced at resolve time, never stored with a price.
 */
export type PricingMenuOptionConfig = Readonly<{
  id: string;
  plan: string;
  /** Set on an option of the per-order plan. */
  qty?: number;
  /** Set on an option of an unlimited plan. */
  vehicleCount?: number;
  highlight?: boolean;
}>;

/** Why a truck count cannot be sold on this unlimited plan; `null` when it can. */
export function truckCountIssue(
  plan: Pick<UnlimitedPlanConfig, 'minTrucks' | 'maxTrucks'>,
  vehicleCount: number,
):
  | { reason: 'below_min_trucks'; minTrucks: number }
  | { reason: 'above_max_trucks'; maxTrucks: number }
  | null {
  if (plan.minTrucks !== undefined && vehicleCount < plan.minTrucks) {
    return { reason: 'below_min_trucks', minTrucks: plan.minTrucks };
  }
  if (plan.maxTrucks !== undefined && vehicleCount > plan.maxTrucks) {
    return { reason: 'above_max_trucks', maxTrucks: plan.maxTrucks };
  }
  return null;
}

/**
 * What one audience on one business is sold and how. Same shape as each
 * audience slice of the `wathiqa_plus_pricing` PostHog payload, which
 * replaces it without a deploy — see `resolveUsagePricingConfig`.
 */
export type SalesConfig = Readonly<{
  flow: PricingFlow;
  /** Plans sold, in the order they are presented. */
  plans: ReadonlyArray<SalesPlanConfig>;
  /** Options of a `menu` flow; ignored by `choose_model`. */
  menu?: ReadonlyArray<PricingMenuOptionConfig>;
}>;

const SALES_CONFIG: Readonly<
  Record<Business, Readonly<Record<SubscriptionAudience, SalesConfig>>>
> = {
  hero: {
    org: {
      flow: 'choose_model',
      plans: [
        { id: 'per_order', kind: 'per_order' },
        { id: 'seats', kind: 'seats' },
      ],
    },
    freelancer: {
      flow: 'choose_model',
      plans: [
        { id: 'per_order', kind: 'per_order' },
        { id: 'seats', kind: 'seats' },
      ],
    },
  },
  wathiqa_plus: {
    org: {
      flow: 'choose_model',
      plans: [
        {
          id: 'unlimited',
          kind: 'unlimited',
          name: { en: 'Unlimited', ar: 'بلا حدود' },
          planCode: 'wathiqa_unlimited',
          perVehiclePricePerMonth: { SAR: 99, USD: 27 },
          taxInclusive: false,
        },
        { id: 'per_order', kind: 'per_order' },
      ],
    },
    freelancer: {
      flow: 'menu',
      plans: [
        { id: 'per_order', kind: 'per_order' },
        {
          id: 'unlimited',
          kind: 'unlimited',
          name: { en: 'Unlimited', ar: 'بلا حدود' },
          planCode: 'wathiqa_unlimited',
          perVehiclePricePerMonth: { SAR: 79, USD: 21 },
          taxInclusive: false,
        },
      ],
      menu: [
        { id: 'pack_5', plan: 'per_order', qty: 5 },
        { id: 'pack_10', plan: 'per_order', qty: 10, highlight: true },
        { id: 'monthly', plan: 'unlimited', vehicleCount: 1 },
      ],
    },
  },
};

export const SUBSCRIPTION_CONFIG = {
  /** Default currency assignment at signup, by org country (ISO 3166-1 alpha-2). */
  defaultCurrencyByCountry: {
    SA: 'SAR',
    // everything else falls back to fallbackCurrency
  } as Readonly<Record<string, Currency>>,

  /**
   * Currency a business quotes in when the buyer's country is unknown
   * (a guest with no account, or a phone we could not place). Read before
   * `fallbackCurrency`: Wathiqa Plus sells Saudi government waybills, so a
   * stranger on that line is priced in SAR rather than USD.
   */
  defaultCurrencyByBusiness: {
    wathiqa_plus: 'SAR',
  } as Readonly<Partial<Record<Business, Currency>>>,

  /** Currency used for any country not listed in `defaultCurrencyByCountry`. */
  fallbackCurrency: 'USD' as Currency,

  /** Days a past_due subscription stays usable before being blocked. */
  graceDays: 7,

  /** Retry attempts after a failed renewal charge, in days since failure. */
  retrySchedule: [1, 3, 7] as const,

  /** Default trial length in days for new subscriptions. */
  trialDays: 14,

  /** Days from finalize to due date on a Billing. Set to 0 to charge immediately. */
  dueDateOffsetDays: 0,

  /**
   * Committed-vehicle-seats sizing for paid plans.
   *
   * - `min` is the floor any paid plan must commit to. The checkout UI
   *   clamps seeds below this (e.g. a trial subscription sitting at
   *   `committedVehicleQty: 1` is auto-selected to `min` when the user
   *   opens "Change plan").
   * - `default` is the pre-selected committed size for fresh signups
   *   with no live subscription (popular fleet size that lands the
   *   user mid-slider so both directions are exposed).
   * - `snapPoints` drive the checkout slider's stops. The last value is
   *   treated as the "plus" gateway — landing on it reveals a free-form
   *   number input. Keep this short by design; labeling every stop
   *   looks crowded.
   */
  committedVehicleQty: {
    min: 15,
    default: 25,
    snapPoints: [15, 25, 35, 50, 75, 100] as const,
  },

  /**
   * Buying order credits — the `usage` model.
   *
   * The counterpart of `committedVehicleQty` for the prepaid bucket,
   * except nothing here is a *commitment*: these are the sizes offered
   * and the rules for spending what was bought.
   */
  usage: {
    /** The volume ladders. See {@link USAGE_ORDER_TIERS}. */
    orderTiers: USAGE_ORDER_TIERS,
    /**
     * The purchase a picker opens on. Sits at the second step so both
     * directions are visible — a buyer who lands on the floor only ever
     * sees the ladder going up, and reads that as a lure.
     */
    defaultQty: 100,
    /**
     * Sizes the checkout offers as one tap. The tier edges plus a
     * halfway stop between the widest two, so the ladder's own argument
     * is legible in the control the buyer is using.
     */
    snapPoints: [10, 50, 100, 250, 500, 1000] as const,
    /**
     * What a quoted ladder shows besides the rate — the same knobs for
     * the chat card, the WhatsApp template line, and the web picker, so
     * every surface argues the price the same way.
     *
     * Presentation rather than pricing, which is why it sits apart from
     * {@link USAGE_ORDER_TIERS}: turning the discount callout off must
     * not be a thing anyone does by editing a price.
     */
    ladderDisplay: {
      /**
       * Print each rung's discount against the entry rate ("save 29%").
       * The ladder's whole argument is that volume is cheaper, and a
       * reader comparing 7 to 5 in a foreign currency does not do that
       * arithmetic. Off when a line would rather not draw attention to
       * its entry rate being the expensive one.
       */
      showSavings: true,
      /**
       * Print what the rung's floor costs as a bundle ("100 orders =
       * 500"). Off by default: it answers a question the buyer did not
       * ask with a number that is not their quantity, and two money
       * figures per rung is what made the first version of this card
       * unreadable.
       *
       * Ignored when the audience's `displayMode` is `packaged` — that
       * mode already leads with the pack total.
       */
      showEntryTotal: false,
    },
    /**
     * How long after a credit is captured it can still be handed back —
     * see `SubscriptionCreditsService.reverse`.
     *
     * Deliberately the same 24 hours as `orderMeter.cancelGraceHours`,
     * and for the same reason: typing a lane and immediately undoing it
     * is a mistake, not a shipment. Beyond the window a driver has been
     * dispatched against it and the credit is spent. A Bayan waybill
     * ends the window early whatever the clock says — TGA has the
     * document and cancelling the order does not take it back.
     */
    reversalGraceHours: 24,
  },

  /** The paywall. See {@link ENTITLEMENT_GATE}. */
  entitlementGate: ENTITLEMENT_GATE,

  /**
   * Signup free-credit policy, per business x audience. The dynamic,
   * per-audience successor to `entitlementGate.freeAllowance`. See
   * {@link FREE_CREDITS} and `resolveFreeCreditsPolicy`.
   */
  freeCredits: FREE_CREDITS,

  /** Which offers each audience is sold and how. See {@link SALES_CONFIG}. */
  sales: SALES_CONFIG,

  /**
   * How the order meter decides an order was really "used".
   *
   * A `per_order` plan bills what the customer put through the
   * platform, and the honest definition of that is not "rows created".
   * Two rules make the number defensible:
   *
   * - **A quick cancel is a mistake, not a shipment.** Typing a lane
   *   and immediately undoing it should not appear on an invoice.
   *   `cancelGraceHours` is how long that window stays open; cancel
   *   later and it counts, because by then a driver was dispatched
   *   against it.
   * - **A government document is permanent.** Once a Bayan waybill
   *   exists, TGA has it and Hero did the work — cancelling the order
   *   afterwards does not take that back, so it bills regardless of
   *   when the cancel lands.
   *
   * The renewal read is shifted back by the same window (see
   * `SubscriptionService.countMeteredUsage`) so no order is ever
   * metered while its grace is still open.
   */
  orderMeter: {
    cancelGraceHours: 24,
  },

  /**
   * Tax rules per currency. SAR -> 15% Saudi VAT. USD -> none in v1
   * (US sales tax / EU VAT compliance is a separate problem). Show
   * "tax not included; consult your local jurisdiction" on USD billings.
   */
  taxByCurrency: {
    SAR: { id: 'saudi_vat', percentage: 0.15 } as TaxInfo,
    USD: null,
  } as Readonly<Partial<Record<Currency, TaxInfo | null>>>,

  plans: {
    free: {
      unlisted: true,
      features: {
        maxVehicles: 2,
        maxVehiclesPolicy: 'hard',
        modules: { order: true, fleet: false, crm: false },
        supportTier: 'email',
      },
    },
    starter: {
      features: {
        maxVehiclesPolicy: 'soft',
        modules: { order: true, fleet: true },
        supportTier: 'standard',
      },

      // basePricePerMonth is used for the one-time price not per vehicle ... we might later needed it for addons.
      // basePricePerMonth: {
      //   SAR: { month: 299, semester: 270, year: 250 },
      //   USD: { month: 79, semester: 430 / 6, year: 800 / 12 },
      // },
      perVehiclePricePerMonth: {
        SAR: { month: 35, semester: 30, year: 25 },
        USD: { month: 15, semester: 12, year: 10 },
      },
      // overagePerVehiclePerMonth: {
      //   SAR: { month: 75, semester: 400 / 6, year: 62.5 },
      //   USD: { month: 22, semester: 20, year: 220 / 12 },
      // },
    },
    growth: {
      recommended: true,
      features: {
        maxVehiclesPolicy: 'soft',
        modules: { order: true, fleet: true, crm: true },
        supportTier: 'priority',
      },
      // basePricePerMonth is used for the one-time price not per vehicle ... we might later needed it for addons.
      // basePricePerMonth: {
      //   SAR: { month: 499, semester: 450, year: 5000 / 12 },
      //   USD: { month: 129, semester: 700 / 6, year: 1300 / 12 },
      // },
      perVehiclePricePerMonth: {
        SAR: { month: 70, semester: 60, year: 49 },
        USD: { month: 22, semester: 20, year: 17 },
      },
      // overagePerVehiclePerMonth: {
      //   SAR: { month: 120, semester: 650 / 6, year: 100 },
      //   USD: { month: 32, semester: 175 / 6, year: 320 / 12 },
      // },
    },
    /**
     * ── Per-order plans ────────────────────────────────────────────
     *
     * For operators who cannot justify a seat commitment — the carrier
     * who ships twice a week, and the whole reason the Wathiqa Plus funnel
     * can quote a three-truck company. They sit alongside `starter` /
     * `growth` rather than replacing them; the two bases serve
     * different buyers.
     *
     * **These plans carry no prices.** A per-order plan is a feature
     * grant, and what it costs to reach is set by the volume ladder in
     * {@link USAGE_ORDER_TIERS} — buying 1000 credits both prices at 3
     * and lands the buyer on `max`. Keeping a second price here would
     * be two answers to "what does an order cost", and the ladder is
     * the one the customer is shown.
     *
     * There is no overage either. A prepaid bucket cannot be exceeded:
     * the credit is checked before the work, so running out is a
     * purchase prompt rather than an invoice line.
     */
    start: {
      features: {
        maxOrdersPolicy: 'soft',
        modules: { order: true, fleet: true },
        supportTier: 'standard',
      },
    },
    pro: {
      recommended: true,
      features: {
        maxOrdersPolicy: 'soft',
        modules: { order: true, fleet: true, crm: true },
        supportTier: 'priority',
      },
    },
    max: {
      features: {
        maxOrdersPolicy: 'soft',
        modules: { order: true, fleet: true, crm: true, api: true },
        integrations: ['api'],
        supportTier: 'dedicated',
      },
    },
    /**
     * ── Wathiqa Plus per-order plans ───────────────────────────────────
     *
     * The white-label ladder, sold only on the WhatsApp line. It exists
     * as its own set of plan codes rather than a discount on the Hero
     * ladder because the two are different products to their buyers:
     * Wathiqa Plus sells government waybills to someone who will never open
     * the dashboard, and its price has to be able to move without
     * repricing Hero.
     *
     * Priced by their own ladder in {@link USAGE_ORDER_TIERS}, seeded
     * at the same numbers the Hero one carries so splitting them
     * changes nothing commercially until somebody edits one.
     *
     * Modules are narrower than the Hero twins on purpose. The pitch is
     * one job done well; unlocking CRM for a carrier who signed up to
     * print a وثيقة is a feature they never find and a support ticket
     * when they do.
     */
    wathiqa_start: {
      features: {
        maxOrdersPolicy: 'soft',
        modules: { order: true, fleet: true },
        integrations: ['whatsapp'],
        supportTier: 'standard',
      },
    },
    wathiqa_pro: {
      recommended: true,
      features: {
        maxOrdersPolicy: 'soft',
        modules: { order: true, fleet: true, crm: true },
        integrations: ['whatsapp'],
        supportTier: 'priority',
      },
    },
    wathiqa_max: {
      features: {
        maxOrdersPolicy: 'soft',
        modules: { order: true, fleet: true, crm: true, api: true },
        integrations: ['whatsapp', 'api'],
        supportTier: 'dedicated',
      },
    },
    /**
     * Unlimited waybills for a monthly price per truck — the `period`
     * counterpart of the Wathiqa Plus credit ladder.
     *
     * The rate below is only the fallback a renewal reads when a
     * subscription carries no `pricingOverride`. What a buyer is quoted
     * comes from the audience's unlimited plans in `SALES_CONFIG` (overlaid
     * by PostHog), and checkout stamps that number on the subscription.
     * Monthly only: every interval carries the same rate so a stray
     * semester / year request cannot price at zero.
     */
    wathiqa_unlimited: {
      features: {
        unlimitedOrders: true,
        maxVehiclesPolicy: 'soft',
        modules: { order: true, fleet: true },
        integrations: ['whatsapp'],
        supportTier: 'priority',
      },
      perVehiclePricePerMonth: {
        SAR: { month: 99, semester: 99, year: 99 },
        USD: { month: 27, semester: 27, year: 27 },
      },
    },
    /**
     * `custom` is a placeholder for negotiated enterprise deals. The
     * actual prices live on `Subscription.pricingOverride` (which the
     * pricing service falls back to when planCode === 'custom').
     */
    custom: {
      contactSales: true,
      features: {
        maxVehiclesPolicy: 'soft',
        modules: { order: true, fleet: true, crm: true, api: true },
        supportTier: 'dedicated',
      },
    },
  } as Readonly<Record<string, PlanConfig>>,

  addons: {
    whatsapp: {
      type: 'recurring_or_one_time',
      // `oneTime` is a flat one-shot price. `month` / `semester` /
      // `year` are **per-month** rates at that commitment cadence;
      // recurring billings multiply by monthsInInterval.
      pricing: {
        SAR: { oneTime: 1200, month: 100, semester: 90, year: 1000 / 12 },
        USD: { oneTime: 320, month: 25, semester: 140 / 6, year: 260 / 12 },
      },
      features: { integrations: ['whatsapp'] },
    },
    api_access: {
      type: 'recurring_only',
      pricing: {
        SAR: { month: 200, semester: 180, year: 2000 / 12 },
        USD: { month: 50, semester: 45, year: 500 / 12 },
      },
      features: { modules: { api: true }, integrations: ['api'] },
    },
  } as Readonly<Record<string, AddonConfig>>,
} as const;

export type PlanCode = keyof typeof SUBSCRIPTION_CONFIG.plans | string;
export type AddonCode = keyof typeof SUBSCRIPTION_CONFIG.addons | string;

/** Whether `planCode` may be offered on `business`. */
export function isPlanOnBusiness(planCode: string, business: Business): boolean {
  return getPlanBusiness(planCode) === business;
}

/** Whether a chooser has to send this plan to a human to be priced. */
export function isContactSalesPlan(planCode: string): boolean {
  return SUBSCRIPTION_CONFIG.plans[planCode]?.contactSales === true;
}

/**
 * The plan codes a chooser may show on a business, in catalog order.
 *
 * The single source for "what can this person buy" — the dashboard
 * grid, the agent's quotes, and anything else that lists plans. Before
 * this, the agent kept its own `QUOTABLE_PLAN_CODES` array and the
 * checkout kept its own `HIDDEN_PLAN_CODES` set, so a new plan appeared
 * in one place and not the other until somebody noticed.
 *
 * Defaults to the self-serve set. Pass `includeContactSales` on a
 * surface that can actually route to a human — the dashboard grid does,
 * a WhatsApp quote does not.
 */
export function getPlansForBusiness(
  business: Business,
  options?: { includeContactSales?: boolean },
): string[] {
  return Object.keys(SUBSCRIPTION_CONFIG.plans).filter(code => {
    const plan = SUBSCRIPTION_CONFIG.plans[code];
    if (!plan || plan.unlisted) return false;
    if (plan.contactSales && !options?.includeContactSales) return false;
    return isPlanOnBusiness(code, business);
  });
}

/** Whether this plan is the tier its ladder leads with. */
export function isRecommendedPlan(planCode: string): boolean {
  return SUBSCRIPTION_CONFIG.plans[planCode]?.recommended === true;
}

/**
 * The plan a picker should open on for a business and unit — the
 * recommended tier, or the first sellable one if none is marked.
 *
 * `undefined` when the business sells nothing on that unit, which
 * callers should read as "don't offer this unit" rather than falling
 * back to the other one behind the user's back.
 */
export function getDefaultPlanForBusiness(
  business: Business,
  unit: BillingUnit,
): string | undefined {
  const candidates = getPlansForBusiness(business).filter(code => planUnit(code) === unit);
  return candidates.find(isRecommendedPlan) ?? candidates[0];
}

/** Which lifecycle a plan is sold on — see `SubscriptionModel` in `@hero/schema`. */
export function getPlanSubscriptionModel(planCode: string): 'period' | 'usage' {
  return planUnit(planCode) === 'order' ? 'usage' : 'period';
}

/** Whether buying this plan tops up a credit bucket rather than starting a period. */
export function isUsagePlan(planCode: string): boolean {
  return getPlanSubscriptionModel(planCode) === 'usage';
}

/**
 * Whether this audience's published `pricePerUnit` already includes
 * VAT. Lives on the same {@link UsageAudiencePricing} object as the
 * rungs so a price edit does not mean opening a second table.
 */
export function usageTaxConfig(
  business: Business,
  audience: SubscriptionAudience = DEFAULT_SUBSCRIPTION_AUDIENCE,
): { taxInclusive: boolean } {
  return {
    taxInclusive: SUBSCRIPTION_CONFIG.usage.orderTiers[business]?.[audience]?.taxInclusive ?? false,
  };
}

/**
 * The catalog signup free-credit policy for one audience on a business.
 *
 * Falls back to the legacy `entitlementGate.freeAllowance` as a
 * repayable grant when a business/audience has no explicit entry, so
 * the old single-number behaviour survives. The PostHog overlay is
 * applied on top of this by `resolveFreeCreditsPolicy`.
 */
export function freeCreditsPolicy(
  business: Business,
  audience: SubscriptionAudience = DEFAULT_SUBSCRIPTION_AUDIENCE,
): FreeCreditsPolicy {
  return (
    SUBSCRIPTION_CONFIG.freeCredits[business]?.[audience] ?? {
      qty: SUBSCRIPTION_CONFIG.entitlementGate.freeAllowance,
      mode: 'repayable',
    }
  );
}

/**
 * The catalog sales config for one audience on a business. A business
 * with no entry sells the ladder and its seat plans through
 * `choose_model`. The PostHog overlay is applied on top by
 * `resolveUsagePricingConfig`.
 */
export function salesConfig(
  business: Business,
  audience: SubscriptionAudience = DEFAULT_SUBSCRIPTION_AUDIENCE,
): SalesConfig {
  return (
    SUBSCRIPTION_CONFIG.sales[business]?.[audience] ?? {
      flow: 'choose_model',
      plans: [
        { id: 'per_order', kind: 'per_order' },
        { id: 'seats', kind: 'seats' },
      ],
    }
  );
}

/** The seat plan an unlimited sales plan bills on when it names none. */
export function defaultUnlimitedPlanCode(business: Business): string | undefined {
  return getPlansForBusiness(business).find(planHasUnlimitedOrders);
}

/**
 * Whether a subscription on this plan covers every order with no credit
 * spent — the per-truck "unlimited" plan. Both halves of the paywall
 * (the entitlement check and the credit capture) read it, so they cannot
 * disagree about who pays per order.
 */
export function planHasUnlimitedOrders(planCode: string | undefined): boolean {
  return Boolean(planCode && SUBSCRIPTION_CONFIG.plans[planCode]?.features?.unlimitedOrders);
}

/**
 * Whether the card leads with the unit rate or the pack total for this
 * audience. Presentation only — see {@link UsagePriceDisplayMode}.
 */
export function usagePriceDisplayMode(
  business: Business,
  audience: SubscriptionAudience = DEFAULT_SUBSCRIPTION_AUDIENCE,
): UsagePriceDisplayMode {
  return SUBSCRIPTION_CONFIG.usage.orderTiers[business]?.[audience]?.displayMode ?? 'per_unit';
}

/**
 * The volume ladder one audience sees on a business, ascending. See
 * {@link USAGE_ORDER_TIERS}.
 *
 * Takes a business: every caller — the checkout, the agent's quote
 * tools, the entitlement gate — knows which brand the buyer is on. An
 * empty array is the answer for a business that sells no credits.
 *
 * `audience` is optional across this whole family of selectors so that
 * the company path — every caller that existed before freelancers
 * could buy — reads the right table without passing anything. Only
 * code that knows it is serving a freelancer has to say so.
 */
export function usageOrderTiers(
  business: Business,
  audience: SubscriptionAudience = DEFAULT_SUBSCRIPTION_AUDIENCE,
  overlay?: ReadonlyArray<UsageTierConfig>,
): ReadonlyArray<UsageTierConfig> {
  if (overlay && overlay.length > 0) return overlay;
  return SUBSCRIPTION_CONFIG.usage.orderTiers[business]?.[audience]?.tiers ?? [];
}

/**
 * The smallest purchase this ladder will sell — its floor.
 *
 * Derived from the first step rather than stored next to it, because a
 * separate minimum is a second source of truth for the same number: set
 * it to 10 while the ladder starts at 25 and the checkout offers a
 * quantity that has no price.
 */
export function minUsagePurchaseQty(
  business: Business,
  audience: SubscriptionAudience = DEFAULT_SUBSCRIPTION_AUDIENCE,
  overlay?: ReadonlyArray<UsageTierConfig>,
): number {
  return usageOrderTiers(business, audience, overlay)[0]?.minQty ?? 1;
}

/**
 * One rung of the ladder as a buyer reads it: a quantity *window* and
 * the rate inside it.
 */
export interface UsagePricingStep {
  fromQty: number;
  /** `undefined` on the top rung — "1000 or more" has no ceiling. */
  toQty?: number;
  planCode: string;
  /** `undefined` when this currency has no price on this rung. */
  pricePerUnit?: number;
  /**
   * How much cheaper this rung is than the entry rung, as a decimal
   * (`0.29` → "save 29%"). `undefined` on the entry rung itself and
   * whenever either rate is missing in the asked-for currency.
   *
   * Derived here rather than in each renderer for the same reason
   * `toQty` is: it is the ladder's sales argument, and a chat card and a
   * web picker disagreeing about whether 100 credits save 28% or 29% is
   * a pricing dispute with a customer.
   */
  savingsVsEntry?: number;
  /** The rung config flags as the one to point at — {@link UsageTierConfig.highlight}. */
  highlight?: boolean;
}

/**
 * The ladder in the shape a price list is read in — "10–99 at 7,
 * 100–249 at 5" — rather than the shape it is stored in.
 *
 * The stored config only carries each rung's floor, because the ceiling
 * is not independent information: it is the next rung's floor minus
 * one. Deriving it here rather than in each renderer is what stops the
 * chat card, the web plan picker, and the tool payload from disagreeing
 * about whether the second rung starts at 100 or at 101.
 */
export function usagePricingLadder(input: {
  business: Business;
  audience?: SubscriptionAudience;
  currency: Currency;
  tiers?: ReadonlyArray<UsageTierConfig>;
}): UsagePricingStep[] {
  const tiers = usageOrderTiers(input.business, input.audience, input.tiers);
  const entryRate = tiers[0]?.pricePerUnit[input.currency];
  return tiers.map((tier, index) => {
    const pricePerUnit = tier.pricePerUnit[input.currency];
    return {
      fromQty: tier.minQty,
      toQty: tiers[index + 1] ? tiers[index + 1].minQty - 1 : undefined,
      planCode: tier.planCode,
      pricePerUnit,
      savingsVsEntry:
        index > 0 && entryRate && pricePerUnit !== undefined && pricePerUnit < entryRate
          ? (entryRate - pricePerUnit) / entryRate
          : undefined,
      highlight: tier.highlight,
    };
  });
}

/**
 * The ladder step a purchase of `qty` reaches — the last one whose
 * `minQty` it clears.
 *
 * Below the floor it still answers the first step rather than
 * `undefined`, so a caller quoting 5 credits gets the honest "that is
 * priced at 7 each" instead of nothing to render. Refusing the
 * purchase is a separate decision, and it belongs to whoever is taking
 * the order — see {@link minUsagePurchaseQty}.
 */
export function usageTierForQty(
  business: Business,
  qty: number,
  audience: SubscriptionAudience = DEFAULT_SUBSCRIPTION_AUDIENCE,
  overlay?: ReadonlyArray<UsageTierConfig>,
): UsageTierConfig | undefined {
  const tiers = usageOrderTiers(business, audience, overlay);
  let reached = tiers[0];
  for (const tier of tiers) {
    if (qty >= tier.minQty) reached = tier;
  }
  return reached;
}

/**
 * What one credit costs at the volume being bought. `undefined` when
 * the business does not sell credits in that currency.
 */
export function usagePricePerUnit(
  business: Business,
  qty: number,
  currency: Currency,
  audience: SubscriptionAudience = DEFAULT_SUBSCRIPTION_AUDIENCE,
  overlay?: ReadonlyArray<UsageTierConfig>,
): number | undefined {
  return usageTierForQty(business, qty, audience, overlay)?.pricePerUnit[currency];
}

/**
 * Where a plan sits on its business's ladder, for comparing two grants.
 * `-1` for anything not on it (`free`, `custom`, the per-vehicle plans).
 */
function usagePlanRank(
  business: Business,
  planCode: string | undefined,
  audience: SubscriptionAudience,
  overlay?: ReadonlyArray<UsageTierConfig>,
): number {
  if (!planCode) return -1;
  return usageOrderTiers(business, audience, overlay).findIndex(tier => tier.planCode === planCode);
}

/**
 * The plan an org should hold after buying `qty` credits — the better
 * of what the purchase earns and what they already have.
 *
 * Buying small never costs someone features they have already paid
 * for. Somebody who bought 1000 and reached `max`, then tops up 10 to
 * finish the month, pays the 10-credit rate and stays on `max`; the
 * alternative punishes exactly the customers who buy the most, and
 * teaches everyone else to hoard credits rather than top up when they
 * need to.
 *
 * The comparison is by ladder position rather than by price, because
 * the price of a purchase and the value of a grant are different
 * things: a small top-up has the *highest* unit price on the ladder
 * and the lowest tier.
 */
export function resolveUsagePlanUpgrade(input: {
  business: Business;
  qty: number;
  /** What they hold now, if anything. */
  currentPlanCode?: string;
  audience?: SubscriptionAudience;
  tiers?: ReadonlyArray<UsageTierConfig>;
}): string {
  const audience = input.audience ?? DEFAULT_SUBSCRIPTION_AUDIENCE;
  const earned = usageTierForQty(input.business, input.qty, audience, input.tiers)?.planCode;
  const heldRank = usagePlanRank(input.business, input.currentPlanCode, audience, input.tiers);
  const earnedRank = usagePlanRank(input.business, earned, audience, input.tiers);
  if (heldRank > earnedRank && input.currentPlanCode) return input.currentPlanCode;
  return earned ?? input.currentPlanCode ?? 'free';
}

/**
 * The committed-quantity sizing block for a billing unit — what the
 * checkout stepper clamps and snaps to, and what a fresh signup is
 * seeded with.
 *
 * Only meaningful for `vehicle` now. An order-credit buyer commits to
 * nothing; they pick a purchase size from
 * `SUBSCRIPTION_CONFIG.usage.snapPoints` instead, which is a different
 * question with a different floor.
 */
export function committedQtyConfigForUnit(
  unit: BillingUnit,
  business: Business = DEFAULT_SUBSCRIPTION_BUSINESS,
  audience: SubscriptionAudience = DEFAULT_SUBSCRIPTION_AUDIENCE,
) {
  return unit === 'order'
    ? {
        min: minUsagePurchaseQty(business, audience),
        default: SUBSCRIPTION_CONFIG.usage.defaultQty,
        snapPoints: SUBSCRIPTION_CONFIG.usage.snapPoints,
      }
    : SUBSCRIPTION_CONFIG.committedVehicleQty;
}

/** The plan's per-seat, per-month rate at one commitment interval. */
function planSeatRate(
  planCode: string,
  interval: SubscriptionInterval,
  currency: Currency,
): number | undefined {
  return SUBSCRIPTION_CONFIG.plans[planCode]?.perVehiclePricePerMonth?.[currency]?.[interval];
}

/**
 * How much cheaper a longer commitment is than paying monthly, as a
 * whole percentage — the "20% OFF" badge on the interval toggle.
 *
 * Derived from the plan's own rates rather than a marketing constant.
 * The badge used to be a hardcoded 15 / 30, which meant editing a
 * price in this file quietly turned the badge into a lie: the number
 * the customer was promised and the number they were charged came from
 * two different places.
 *
 * `undefined` when the plan has no rate to compare (monthly itself,
 * `free`, `custom`, an order-credit plan, or a currency the plan isn't
 * priced in) — callers render no badge rather than "0% OFF".
 *
 * Order-credit plans always answer `undefined` because they have no
 * interval to save on. Their discount is for volume, not duration, and
 * it is quoted by the ladder — see {@link usagePricePerUnit}.
 */
export function intervalSavingsPercent(
  planCode: string,
  interval: SubscriptionInterval,
  currency: Currency = 'SAR',
): number | undefined {
  if (interval === 'month') return undefined;
  const monthly = planSeatRate(planCode, 'month', currency);
  const atInterval = planSeatRate(planCode, interval, currency);
  if (!monthly || atInterval === undefined || atInterval >= monthly) return undefined;
  return Math.round(((monthly - atInterval) / monthly) * 100);
}

/**
 * Resolve the default currency for an org based on its country code.
 * Returns `fallbackCurrency` when the country isn't explicitly mapped.
 */
export function defaultCurrencyForCountry(country: string | undefined): Currency {
  if (!country) return SUBSCRIPTION_CONFIG.fallbackCurrency;
  return (
    SUBSCRIPTION_CONFIG.defaultCurrencyByCountry[country] ?? SUBSCRIPTION_CONFIG.fallbackCurrency
  );
}

/** ISO 3166-1 alpha-2 country of a phone number, or `undefined` when it cannot be placed. */
export function countryFromPhone(phone: string | undefined): string | undefined {
  const digits = phone?.trim();
  if (!digits) return undefined;
  const e164 = digits.startsWith('+') ? digits : `+${digits.replace(/^00/, '')}`;
  return parsePhoneNumberFromString(e164)?.country;
}

/**
 * Currency to quote someone who has no subscription owner yet.
 *
 * Their phone's country wins when that country has a mapped currency;
 * otherwise the business's own default (`defaultCurrencyByBusiness`);
 * otherwise `fallbackCurrency`.
 */
export function resolveGuestCurrency(input: {
  business: Business;
  phone?: string;
}): Currency {
  const country = countryFromPhone(input.phone);
  const byCountry = country ? SUBSCRIPTION_CONFIG.defaultCurrencyByCountry[country] : undefined;
  return (
    byCountry ??
    SUBSCRIPTION_CONFIG.defaultCurrencyByBusiness[input.business] ??
    SUBSCRIPTION_CONFIG.fallbackCurrency
  );
}
